// PhotoViewer.tsx — tap a photo, see it properly. The fleet's shared image viewer.
//
// Bogdan 2026-07-09: "in all apps can people click and have the image go big on screen, manipulate,
// close the image like expected — zoom in, zoom out, rotate??? we totally missed this feature."
//
// A photo the user took is a *thing they look at*. Showing it as a 64px thumbnail with no way to
// open it is the app refusing to do its job. Expected behaviour, and what this implements:
//   • tap a thumbnail            → full-screen, on a dark backdrop
//   • pinch                      → zoom (1×–6×), two fingers
//   • double-tap                 → toggle 1× ⇄ 2.5× at the tapped point
//   • drag when zoomed           → pan
//   • rotate button              → 90° steps (photographed pages are often sideways)
//   • ✕ / Esc / tap the backdrop → close (backdrop only when not zoomed, so panning can't close it)
//   • ‹ › when there are several → step through them
//
// COPY THIS FILE VERBATIM into any app whose subject is a thing you look at. It takes plain
// `src` strings, so it works with blob: URLs (native, bytes over IPC) or data: URLs (web).
import { useCallback, useEffect, useRef, useState } from "react";

export interface ViewerPhoto {
  /** Anything renderable by <img src>: a blob: URL, a data: URL, or an http(s) URL. */
  src: string;
  alt: string;
}

const MIN_SCALE = 1;
const MAX_SCALE = 6;
const DOUBLE_TAP_SCALE = 2.5;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const dist = (a: React.Touch, b: React.Touch) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

export function PhotoViewer({ photos, startIndex = 0, onClose }: {
  photos: ViewerPhoto[];
  startIndex?: number;
  onClose: () => void;
}) {
  const [i, setI] = useState(startIndex);
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const [rot, setRot] = useState(0);

  // Gesture bookkeeping — refs, not state: these change every touchmove and must not re-render.
  const pinch = useRef<{ d0: number; s0: number } | null>(null);
  const pan = useRef<{ x: number; y: number; tx: number; ty: number } | null>(null);
  const lastTap = useRef(0);

  const reset = useCallback(() => { setScale(1); setTx(0); setTy(0); setRot(0); }, []);
  const go = useCallback((next: number) => { setI((n) => clamp(next, 0, photos.length - 1)); reset(); }, [photos.length, reset]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") go(i + 1);
      else if (e.key === "ArrowLeft") go(i - 1);
    };
    document.addEventListener("keydown", onKey);
    // Lock the page behind the viewer so the body doesn't scroll under it.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [onClose, go, i]);

  const photo = photos[i];
  if (!photo) return null;

  function onTouchStart(e: React.TouchEvent) {
    if (e.touches.length === 2) {
      pinch.current = { d0: dist(e.touches[0], e.touches[1]), s0: scale };
      pan.current = null;
      return;
    }
    if (e.touches.length === 1) {
      const now = Date.now();
      if (now - lastTap.current < 280) {          // double tap
        setScale((s) => (s > 1.05 ? 1 : DOUBLE_TAP_SCALE));
        if (scale > 1.05) { setTx(0); setTy(0); }
        lastTap.current = 0;
        return;
      }
      lastTap.current = now;
      if (scale > 1) pan.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, tx, ty };
    }
  }

  function onTouchMove(e: React.TouchEvent) {
    if (pinch.current && e.touches.length === 2) {
      const d = dist(e.touches[0], e.touches[1]);
      setScale(clamp(pinch.current.s0 * (d / pinch.current.d0), MIN_SCALE, MAX_SCALE));
      return;
    }
    if (pan.current && e.touches.length === 1) {
      setTx(pan.current.tx + (e.touches[0].clientX - pan.current.x));
      setTy(pan.current.ty + (e.touches[0].clientY - pan.current.y));
    }
  }

  function onTouchEnd(e: React.TouchEvent) {
    if (e.touches.length === 0) { pinch.current = null; pan.current = null; }
    if (scale <= 1.02) { setScale(1); setTx(0); setTy(0); }   // snap back
  }

  const BTN: React.CSSProperties = {
    width: 44, height: 44, borderRadius: 999, border: "1px solid rgba(255,255,255,.25)",
    background: "rgba(0,0,0,.45)", color: "#fff", fontSize: 18, lineHeight: 1, cursor: "pointer",
    display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0,
  };

  return (
    <div
      role="dialog" aria-modal="true" aria-label={photo.alt}
      onClick={() => { if (scale === 1) onClose(); }}   // a zoomed photo must not close on a stray tap
      style={{
        position: "fixed", inset: 0, zIndex: 100, background: "rgba(0,0,0,.92)",
        display: "grid", placeItems: "center", touchAction: "none", overscrollBehavior: "contain",
      }}
    >
      <img
        src={photo.src} alt={photo.alt} draggable={false}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
        style={{
          maxWidth: "100%", maxHeight: "100dvh", objectFit: "contain", userSelect: "none",
          transform: `translate(${tx}px, ${ty}px) scale(${scale}) rotate(${rot}deg)`,
          transition: pinch.current || pan.current ? "none" : "transform .18s ease-out",
          willChange: "transform",
        }}
      />

      {/* Controls sit above the home indicator and below the notch. */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "absolute", left: 0, right: 0,
          bottom: "calc(1rem + env(safe-area-inset-bottom))",
          display: "flex", gap: 12, justifyContent: "center", alignItems: "center",
        }}
      >
        {photos.length > 1 && <button type="button" style={BTN} aria-label="Previous photo" onClick={() => go(i - 1)} disabled={i === 0}>‹</button>}
        <button type="button" style={BTN} aria-label="Zoom out" onClick={() => setScale((s) => clamp(s - 0.5, MIN_SCALE, MAX_SCALE))}>−</button>
        <button type="button" style={BTN} aria-label="Zoom in" onClick={() => setScale((s) => clamp(s + 0.5, MIN_SCALE, MAX_SCALE))}>＋</button>
        <button type="button" style={BTN} aria-label="Rotate" onClick={() => setRot((r) => (r + 90) % 360)}>⟳</button>
        {photos.length > 1 && <button type="button" style={BTN} aria-label="Next photo" onClick={() => go(i + 1)} disabled={i === photos.length - 1}>›</button>}
      </div>

      <button
        type="button" aria-label="Close" onClick={(e) => { e.stopPropagation(); onClose(); }}
        style={{ ...BTN, position: "absolute", top: "calc(1rem + env(safe-area-inset-top))", right: "1rem" }}
      >✕</button>

      {photos.length > 1 && (
        <div style={{
          position: "absolute", top: "calc(1.4rem + env(safe-area-inset-top))", left: 0, right: 0,
          textAlign: "center", color: "rgba(255,255,255,.75)", fontSize: 13, pointerEvents: "none",
        }}>{i + 1} of {photos.length}</div>
      )}
    </div>
  );
}
